/**
 * Rascunhos de pré-venda + caches leves (vendedores / clientes recentes).
 * Reserva de estoque NÃO acontece aqui — só após sync online.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type PresaleSyncStatus = 'local' | 'pending' | 'synced' | 'error';

export interface PresaleDraftItem {
  id: number;
  name?: string;
  sku?: string;
  barcode?: string;
  quantity: number;
  unitPrice: number;
  originalPrice?: number;
  discount?: number;
  discountType?: string;
  surcharge?: number;
  surchargeType?: string;
  totalPrice?: number;
  price?: number;
  fromQuote?: boolean;
  [key: string]: unknown;
}

export interface PresaleDraft {
  id: string;
  userId: string;
  sellerId: number | null;
  sellerName: string | null;
  customerId: number | null;
  customerSnapshot: Record<string, unknown> | null;
  priceTableId: number | null;
  priceTableName: string | null;
  items: PresaleDraftItem[];
  observations: string;
  importedSaleId: number | null;
  importedQuoteId: number | null;
  importedQuoteNumber: string | null;
  totals?: {
    subtotal: number;
    discount: number;
    surcharge: number;
    final: number;
  };
  syncStatus: PresaleSyncStatus;
  syncError: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface CachedSeller {
  id: number;
  name: string;
  email?: string;
  [key: string]: unknown;
}

export interface CachedCustomer {
  id: number;
  name: string;
  cpf_cnpj?: string;
  email?: string;
  phone?: string;
  cachedAt: number;
  [key: string]: unknown;
}

interface PresaleDb extends DBSchema {
  drafts: {
    key: string;
    value: PresaleDraft;
    indexes: {
      userId: string;
      syncStatus: string;
      updatedAt: number;
    };
  };
  meta: {
    key: string;
    value: { key: string; activeDraftId?: string | null; updatedAt: number };
  };
  sellers: {
    key: number;
    value: CachedSeller;
  };
  customers: {
    key: number;
    value: CachedCustomer;
  };
}

const DB_NAME = 'wfmed_presale_offline';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<PresaleDb>> | null = null;

function newDraftId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function isBrowserOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

export function isNetworkFailure(error: unknown): boolean {
  if (!isBrowserOnline()) return true;
  const err = error as {
    code?: string;
    message?: string;
    response?: unknown;
  };
  if (!err) return true;
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  if (!err.response && err.message) {
    const m = String(err.message).toLowerCase();
    if (m.includes('network') || m.includes('timeout') || m.includes('failed to fetch')) {
      return true;
    }
  }
  return false;
}

async function getDb(): Promise<IDBPDatabase<PresaleDb>> {
  if (!dbPromise) {
    dbPromise = openDB<PresaleDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('drafts')) {
          const store = db.createObjectStore('drafts', { keyPath: 'id' });
          store.createIndex('userId', 'userId');
          store.createIndex('syncStatus', 'syncStatus');
          store.createIndex('updatedAt', 'updatedAt');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('sellers')) {
          db.createObjectStore('sellers', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('customers')) {
          db.createObjectStore('customers', { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

function activeMetaKey(userId: string): string {
  return `active:${userId || 'anon'}`;
}

export async function getActiveDraftId(userId: string): Promise<string | null> {
  const db = await getDb();
  const meta = await db.get('meta', activeMetaKey(userId));
  return meta?.activeDraftId || null;
}

export async function setActiveDraftId(
  userId: string,
  draftId: string | null
): Promise<void> {
  const db = await getDb();
  await db.put('meta', {
    key: activeMetaKey(userId),
    activeDraftId: draftId,
    updatedAt: Date.now(),
  });
}

export async function getDraft(id: string): Promise<PresaleDraft | undefined> {
  const db = await getDb();
  return db.get('drafts', id);
}

export async function putDraft(draft: PresaleDraft): Promise<void> {
  const db = await getDb();
  await db.put('drafts', { ...draft, updatedAt: Date.now() });
}

export async function deleteDraft(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('drafts', id);
}

export async function listDraftsByUser(userId: string): Promise<PresaleDraft[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex('drafts', 'userId', userId);
  return all.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function listPendingDrafts(userId?: string): Promise<PresaleDraft[]> {
  const db = await getDb();
  const pending = await db.getAllFromIndex('drafts', 'syncStatus', 'pending');
  const errors = await db.getAllFromIndex('drafts', 'syncStatus', 'error');
  const list = [...pending, ...errors].sort((a, b) => a.createdAt - b.createdAt);
  if (userId) return list.filter((d) => d.userId === userId);
  return list;
}

export async function getOrCreateActiveDraft(userId: string): Promise<PresaleDraft> {
  const existingId = await getActiveDraftId(userId);
  if (existingId) {
    const existing = await getDraft(existingId);
    if (existing && (existing.syncStatus === 'local' || existing.syncStatus === 'error')) {
      return existing;
    }
  }

  const draft: PresaleDraft = {
    id: newDraftId(),
    userId: String(userId || 'anon'),
    sellerId: null,
    sellerName: null,
    customerId: null,
    customerSnapshot: null,
    priceTableId: null,
    priceTableName: null,
    items: [],
    observations: '',
    importedSaleId: null,
    importedQuoteId: null,
    importedQuoteNumber: null,
    syncStatus: 'local',
    syncError: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await putDraft(draft);
  await setActiveDraftId(userId, draft.id);
  return draft;
}

export async function saveActiveSessionDraft(
  userId: string,
  partial: Partial<PresaleDraft> & { items: PresaleDraftItem[] }
): Promise<PresaleDraft> {
  const current = await getOrCreateActiveDraft(userId);
  // Não sobrescrever draft que já foi enfileirado como pending
  if (current.syncStatus === 'pending') {
    const fresh = await getOrCreateActiveDraft(userId);
    const next: PresaleDraft = {
      ...fresh,
      ...partial,
      id: fresh.id,
      userId: String(userId || 'anon'),
      syncStatus: 'local',
      syncError: null,
      createdAt: fresh.createdAt,
      updatedAt: Date.now(),
    };
    await putDraft(next);
    await setActiveDraftId(userId, next.id);
    return next;
  }

  const next: PresaleDraft = {
    ...current,
    ...partial,
    id: current.id,
    userId: String(userId || 'anon'),
    syncStatus: 'local',
    syncError: null,
    updatedAt: Date.now(),
  };
  await putDraft(next);
  await setActiveDraftId(userId, next.id);
  return next;
}

export async function enqueuePendingDraft(
  userId: string,
  payload: Omit<PresaleDraft, 'id' | 'userId' | 'syncStatus' | 'syncError' | 'createdAt' | 'updatedAt'> & {
    id?: string;
  }
): Promise<PresaleDraft> {
  const draft: PresaleDraft = {
    id: payload.id || newDraftId(),
    userId: String(userId || 'anon'),
    sellerId: payload.sellerId ?? null,
    sellerName: payload.sellerName ?? null,
    customerId: payload.customerId ?? null,
    customerSnapshot: payload.customerSnapshot ?? null,
    priceTableId: payload.priceTableId ?? null,
    priceTableName: payload.priceTableName ?? null,
    items: payload.items || [],
    observations: payload.observations || '',
    importedSaleId: payload.importedSaleId ?? null,
    importedQuoteId: payload.importedQuoteId ?? null,
    importedQuoteNumber: payload.importedQuoteNumber ?? null,
    totals: payload.totals,
    syncStatus: 'pending',
    syncError: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await putDraft(draft);

  // Libera sessão ativa para novo atendimento
  const activeId = await getActiveDraftId(userId);
  if (activeId === draft.id) {
    await setActiveDraftId(userId, null);
  } else {
    // limpa draft local ativo vazio / antigo
    if (activeId) {
      const active = await getDraft(activeId);
      if (active && active.syncStatus === 'local') {
        await deleteDraft(activeId);
      }
    }
    await setActiveDraftId(userId, null);
  }

  return draft;
}

export async function markDraftError(id: string, message: string): Promise<void> {
  const draft = await getDraft(id);
  if (!draft) return;
  await putDraft({
    ...draft,
    syncStatus: 'error',
    syncError: message,
    updatedAt: Date.now(),
  });
}

export async function markDraftSynced(id: string): Promise<void> {
  await deleteDraft(id);
}

export async function clearActiveLocalDraft(userId: string): Promise<void> {
  const activeId = await getActiveDraftId(userId);
  if (activeId) {
    const draft = await getDraft(activeId);
    if (draft && draft.syncStatus === 'local') {
      await deleteDraft(activeId);
    }
  }
  await setActiveDraftId(userId, null);
}

export async function putSellersCache(sellers: CachedSeller[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('sellers', 'readwrite');
  await tx.store.clear();
  await Promise.all([...sellers.map((s) => tx.store.put(s)), tx.done]);
}

export async function getSellersCache(): Promise<CachedSeller[]> {
  const db = await getDb();
  return db.getAll('sellers');
}

export async function putCustomerCache(customer: CachedCustomer): Promise<void> {
  const db = await getDb();
  await db.put('customers', { ...customer, cachedAt: Date.now() });
}

export async function putCustomersCache(customers: CachedCustomer[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('customers', 'readwrite');
  await Promise.all([
    ...customers.map((c) => tx.store.put({ ...c, cachedAt: Date.now() })),
    tx.done,
  ]);
}

export async function searchCustomersCache(term: string): Promise<CachedCustomer[]> {
  const db = await getDb();
  const all = await db.getAll('customers');
  const q = String(term || '').toLowerCase().trim();
  if (!q) {
    return all.sort((a, b) => (b.cachedAt || 0) - (a.cachedAt || 0)).slice(0, 30);
  }
  return all
    .filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        String(c.cpf_cnpj || '').includes(q) ||
        (c.email || '').toLowerCase().includes(q)
    )
    .slice(0, 30);
}
